import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MessageCircle, X, Minus, Trash2, Sparkles } from "lucide-react";
import { ChatMessage } from "./ChatMessage";
import { ChatInput } from "./ChatInput";
import { askAIQuestion } from "@/services/aiService";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

interface AIChatWidgetProps {
  context?: Record<string, any>;
}

const EXAMPLE_QUESTIONS = [
  "What types of deals require pre-approval?",
  "Can athletes work with alcohol brands?",
  "What happens if I violate NCAA rules?",
  "How do I submit a new NIL deal?",
];

export const AIChatWidget = ({ context }: AIChatWidgetProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [queriesRemaining, setQueriesRemaining] = useState(20);
  const { toast } = useToast();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      fetchQueryCount();
      if (messages.length === 0) {
        addWelcomeMessage();
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const fetchQueryCount = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
      const { count } = await supabase
        .from("ai_query_logs")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id)
        .gte("created_at", oneHourAgo);

      setQueriesRemaining(20 - (count || 0));
    } catch (error) {
      console.error("Error fetching query count:", error);
    }
  };

  const addWelcomeMessage = () => {
    setMessages([{
      id: crypto.randomUUID(),
      role: "assistant",
      content: "Hi! I'm NexusAI, your NCAA NIL compliance assistant. Ask me anything about NIL deals, compliance rules, or how to use this platform!",
      timestamp: new Date(),
    }]);
  };

  const handleSendMessage = async (content: string) => {
    if (queriesRemaining <= 0) {
      toast({
        title: "Rate limit reached",
        description: "You've reached the limit of 20 questions per hour. Please try again later.",
        variant: "destructive",
      });
      return;
    }

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: "user",
      content,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const response = await askAIQuestion(content, context);
      
      const assistantMessage: Message = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: response,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
      setQueriesRemaining((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error asking AI:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExampleClick = (question: string) => {
    handleSendMessage(question);
  };

  const handleClearChat = () => {
    setMessages([]);
    addWelcomeMessage();
  };

  const getRateLimitColor = () => {
    if (queriesRemaining >= 15) return "bg-green-500";
    if (queriesRemaining >= 5) return "bg-yellow-500";
    return "bg-red-500";
  };

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        size="icon"
        className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg z-50"
      >
        <MessageCircle className="h-6 w-6" />
      </Button>
    );
  }

  if (isMinimized) {
    return (
      <Card className="fixed bottom-6 right-6 w-80 shadow-2xl z-50 animate-in slide-in-from-bottom-4">
        <CardHeader className="flex flex-row items-center justify-between p-4 cursor-pointer" onClick={() => setIsMinimized(false)}>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">NexusAI Assistant</CardTitle>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="fixed bottom-6 right-6 w-[400px] h-[600px] shadow-2xl z-50 flex flex-col animate-in slide-in-from-bottom-4">
      <CardHeader className="flex flex-row items-center justify-between p-4 border-b">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <CardTitle className="text-base">NexusAI Assistant</CardTitle>
        </div>
        <div className="flex items-center gap-1">
          {messages.length > 1 && (
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleClearChat}>
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setIsMinimized(true)}>
            <Minus className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setIsOpen(false)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      <div className="p-4 border-b bg-muted/50">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Queries remaining</span>
          <Badge variant="secondary" className="gap-2">
            <div className={`h-2 w-2 rounded-full ${getRateLimitColor()}`} />
            {queriesRemaining}/20
          </Badge>
        </div>
      </div>

      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        <div className="space-y-4">
          {messages.map((message) => (
            <ChatMessage
              key={message.id}
              role={message.role}
              content={message.content}
              timestamp={message.timestamp}
            />
          ))}
          
          {isLoading && (
            <div className="flex gap-3">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-primary animate-pulse" />
              </div>
              <Card className="p-3 bg-muted">
                <div className="flex gap-1">
                  <div className="h-2 w-2 rounded-full bg-primary/60 animate-bounce" />
                  <div className="h-2 w-2 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "0.2s" }} />
                  <div className="h-2 w-2 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "0.4s" }} />
                </div>
              </Card>
            </div>
          )}

          {messages.length === 1 && !isLoading && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground px-1">Try asking:</p>
              <div className="flex flex-wrap gap-2">
                {EXAMPLE_QUESTIONS.map((question) => (
                  <Button
                    key={question}
                    variant="outline"
                    size="sm"
                    className="text-xs h-auto py-2 whitespace-normal text-left"
                    onClick={() => handleExampleClick(question)}
                  >
                    {question}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      <ChatInput
        onSend={handleSendMessage}
        disabled={isLoading || queriesRemaining <= 0}
        placeholder={queriesRemaining <= 0 ? "Rate limit reached..." : "Type your question..."}
      />
    </Card>
  );
};
